import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Mail, Plus, Trash2, Send, KeyRound, Check, AlertCircle, Info } from "lucide-react";

const PRESET_LABELS = {
  gmail: "Gmail",
  outlook: "Outlook / Hotmail",
  office365: "Office 365",
  aruba: "Aruba (business)",
  libero: "Libero.it",
  register: "Register.it / Securemail",
  ionos: "IONOS",
  custom: "Personalizzato",
};

const emptyForm = {
  label: "",
  from_email: "",
  from_name: "",
  smtp_host: "",
  smtp_port: 587,
  smtp_user: "",
  smtp_password: "",
  smtp_use_tls: true,
  smtp_use_ssl: false,
};

export default function AccountEmailPage() {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [presets, setPresets] = useState({});
  const [openNew, setOpenNew] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("gmail");
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [testDialog, setTestDialog] = useState(null); // { account, to }
  const [testing, setTesting] = useState(false);
  const [editForm, setEditForm] = useState(null); // account being edited

  const load = async () => {
    const [a, p] = await Promise.all([
      api.get("/smtp-accounts"),
      api.get("/smtp-presets"),
    ]);
    setAccounts(a.data);
    setPresets(p.data);
  };

  useEffect(() => { load(); }, []);

  const applyPreset = (key) => {
    setSelectedPreset(key);
    if (key === "custom") return;
    const p = presets[key];
    if (!p) return;
    setForm((prev) => ({
      ...prev,
      smtp_host: p.host,
      smtp_port: p.port,
      smtp_use_tls: p.use_tls,
      smtp_use_ssl: p.use_ssl || false,
    }));
  };

  const openNewDialog = () => {
    setForm({ ...emptyForm });
    setSelectedPreset("gmail");
    applyPreset("gmail");
    setOpenNew(true);
  };

  const save = async () => {
    if (!form.label || !form.from_email || !form.smtp_host || !form.smtp_user || !form.smtp_password) {
      return toast.error(t("auto.compila_tutti_i_campi_obbligatori"));
    }
    setSaving(true);
    try {
      await api.post("/smtp-accounts", form);
      toast.success(t("auto.account_email_salvato"));
      setOpenNew(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const updateAccount = async () => {
    if (!editForm) return;
    const payload = { ...editForm };
    // Non inviare password vuota
    if (!payload.smtp_password) delete payload.smtp_password;
    delete payload.id;
    delete payload.has_password;
    delete payload.tenant_id;
    delete payload.created_at;
    delete payload.created_by;
    setSaving(true);
    try {
      await api.patch(`/smtp-accounts/${editForm.id}`, payload);
      toast.success("Account aggiornato");
      setEditForm(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const deleteAccount = async (id, label) => {
    if (!window.confirm(`Eliminare l'account "${label}"?`)) return;
    try {
      await api.delete(`/smtp-accounts/${id}`);
      toast.success(t("common.deleted"));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doTest = async () => {
    if (!testDialog?.to) return toast.error(t("auto.inserisci_un_indirizzo_di_test"));
    setTesting(true);
    try {
      await api.post(`/smtp-accounts/${testDialog.account.id}/test`, { to: testDialog.to });
      toast.success(`Email di test inviata a ${testDialog.to}`);
      setTestDialog(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Comunicazioni</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.email_account")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("auto.aggiungi_le_email_di_roberto_miriam_alessia_luca_al_momento_")}</p>
        </div>
        <Button className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" onClick={openNewDialog} data-testid="btn-new-smtp">
          <Plus className="mr-2 h-4 w-4" /> {t("auto.nuovo_account_email")}
        </Button>
      </div>

      <div className="rounded-sm border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <div className="mb-1 font-semibold">{t("auto.come_funziona")}</div>
            <ul className="ml-4 list-disc space-y-0.5">
              <li>{t("auto.configuri_un_account_per_ogni_email_aziendale_una_tantum")}</li>
              <li>Per Gmail serve una <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline">Password app</a> (con la 2FA attiva), non la password normale.</li>
              <li>{t("auto.per_outlookarubalibero_funziona_con_la_password_normale_dell")}</li>
              <li>{t("auto.nel_dialog_di_invio_potrai_scegliere_quale_account_usare_com")}</li>
            </ul>
          </div>
        </div>
      </div>

      {accounts.length === 0 && (
        <div className="rounded-sm border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-400">
          Nessun account configurato. Aggiungine uno con "Nuovo account email".
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {accounts.map((a) => (
          <div key={a.id} className="rounded-sm border border-slate-200 bg-white p-4" data-testid={`smtp-card-${a.id}`}>
            <div className="mb-2 flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-lg font-bold text-slate-900">{a.label}</div>
                <div className="truncate font-mono text-xs text-slate-500">{a.from_email}</div>
              </div>
              <div className="flex items-center gap-1">
                {a.has_password ? (
                  <Badge className="rounded-sm bg-emerald-100 text-emerald-700"><Check className="mr-0.5 h-3 w-3" />{t("auto.ok")}</Badge>
                ) : (
                  <Badge className="rounded-sm bg-red-100 text-red-700"><AlertCircle className="mr-0.5 h-3 w-3" />No PW</Badge>
                )}
              </div>
            </div>
            <div className="space-y-0.5 text-xs text-slate-600">
              <div><b>Nome:</b> {a.from_name || "—"}</div>
              <div><b>SMTP:</b> {a.smtp_host}:{a.smtp_port}</div>
              <div><b>Sicurezza:</b> {a.smtp_use_ssl ? "SSL" : a.smtp_use_tls ? "STARTTLS" : "Nessuna"}</div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              <Button size="sm" variant="outline" onClick={() => setTestDialog({ account: a, to: "" })} className="rounded-sm" data-testid={`smtp-test-${a.id}`}>
                <Send className="mr-1 h-3 w-3" /> Test
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditForm({ ...a, smtp_password: "" })} className="rounded-sm" data-testid={`smtp-edit-${a.id}`}>
                <KeyRound className="mr-1 h-3 w-3" /> {t("auto.modifica")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => deleteAccount(a.id, a.label)} data-testid={`smtp-del-${a.id}`}>
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Dialog nuovo account */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t("auto.nuovo_account_email")}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Etichetta *</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="es. Roberto" className="rounded-sm" data-testid="smtp-label" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Email mittente *</Label>
                <Input type="email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value, smtp_user: form.smtp_user || e.target.value })} placeholder="roberto@tuadomain.it" className="rounded-sm font-mono" data-testid="smtp-from-email" />
              </div>
              <div>
                <Label>Nome visualizzato</Label>
                <Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} placeholder="Roberto Ufficio" className="rounded-sm" data-testid="smtp-from-name" />
              </div>
            </div>
            <div>
              <Label>{t("auto.provider_compila_i_campi_tecnici")}</Label>
              <Select value={selectedPreset} onValueChange={applyPreset}>
                <SelectTrigger className="rounded-sm" data-testid="smtp-preset"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(presets).map((k) => <SelectItem key={k} value={k}>{PRESET_LABELS[k] || k}</SelectItem>)}
                  <SelectItem value="custom">{PRESET_LABELS.custom}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-[1fr_100px] gap-2">
              <div>
                <Label>Host SMTP *</Label>
                <Input value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} className="rounded-sm font-mono" data-testid="smtp-host" />
              </div>
              <div>
                <Label>Porta</Label>
                <Input type="number" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: parseInt(e.target.value) || 587 })} className="rounded-sm font-mono" data-testid="smtp-port" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Utente (di solito la mail) *</Label>
                <Input value={form.smtp_user} onChange={(e) => setForm({ ...form, smtp_user: e.target.value })} className="rounded-sm font-mono" data-testid="smtp-user" />
              </div>
              <div>
                <Label>Password *</Label>
                <Input type="password" value={form.smtp_password} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} className="rounded-sm font-mono" data-testid="smtp-password" />
              </div>
            </div>
            <div className="flex flex-wrap gap-4 text-xs">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.smtp_use_tls} onChange={(e) => setForm({ ...form, smtp_use_tls: e.target.checked })} data-testid="smtp-tls" />
                <span>STARTTLS (porta 587)</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.smtp_use_ssl} onChange={(e) => setForm({ ...form, smtp_use_ssl: e.target.checked })} data-testid="smtp-ssl" />
                <span>SSL diretto (porta 465)</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button onClick={save} disabled={saving} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="smtp-save">
              {saving ? "Salvataggio..." : "Salva account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog modifica */}
      <Dialog open={!!editForm} onOpenChange={(o) => { if (!o) setEditForm(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Modifica account — {editForm?.label}</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-3 text-sm">
              <div><Label>Etichetta</Label><Input value={editForm.label} onChange={(e) => setEditForm({ ...editForm, label: e.target.value })} className="rounded-sm" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Email mittente</Label><Input value={editForm.from_email} onChange={(e) => setEditForm({ ...editForm, from_email: e.target.value })} className="rounded-sm font-mono" /></div>
                <div><Label>{t("common.name")}</Label><Input value={editForm.from_name || ""} onChange={(e) => setEditForm({ ...editForm, from_name: e.target.value })} className="rounded-sm" /></div>
              </div>
              <div className="grid grid-cols-[1fr_100px] gap-2">
                <div><Label>Host SMTP</Label><Input value={editForm.smtp_host} onChange={(e) => setEditForm({ ...editForm, smtp_host: e.target.value })} className="rounded-sm font-mono" /></div>
                <div><Label>Porta</Label><Input type="number" value={editForm.smtp_port} onChange={(e) => setEditForm({ ...editForm, smtp_port: parseInt(e.target.value) || 587 })} className="rounded-sm font-mono" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Utente</Label><Input value={editForm.smtp_user} onChange={(e) => setEditForm({ ...editForm, smtp_user: e.target.value })} className="rounded-sm font-mono" /></div>
                <div><Label>{t("auto.nuova_password_lascia_vuoto_per_non_cambiarla")}</Label><Input type="password" value={editForm.smtp_password || ""} onChange={(e) => setEditForm({ ...editForm, smtp_password: e.target.value })} className="rounded-sm font-mono" /></div>
              </div>
              <div className="flex flex-wrap gap-4 text-xs">
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editForm.smtp_use_tls} onChange={(e) => setEditForm({ ...editForm, smtp_use_tls: e.target.checked })} /> STARTTLS</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!editForm.smtp_use_ssl} onChange={(e) => setEditForm({ ...editForm, smtp_use_ssl: e.target.checked })} /> SSL diretto</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditForm(null)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button onClick={updateAccount} disabled={saving} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="smtp-update">
              {saving ? "Salvataggio..." : "Aggiorna"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog test */}
      <Dialog open={!!testDialog} onOpenChange={(o) => { if (!o) setTestDialog(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Test invio — {testDialog?.account.label}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs">
              Invia una mail di prova da <b>{testDialog?.account.from_email}</b> per verificare che l'account funzioni.
            </div>
            <div>
              <Label>Invia a *</Label>
              <Input
                type="email"
                value={testDialog?.to || ""}
                onChange={(e) => setTestDialog({ ...testDialog, to: e.target.value })}
                placeholder="tua-email-personale@..."
                className="rounded-sm font-mono"
                data-testid="smtp-test-to"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialog(null)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button onClick={doTest} disabled={testing || !testDialog?.to} className="rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid="smtp-test-send">
              {testing ? t("auto.invio_ellipsis") : t("auto.invia_test_btn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
